import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../state/store';
import type { ExtendedThemeConfig, ThemeMode } from './types';
import { createAgGridTheme, createEChartsTheme, createThemeProps, getThemeConfig } from './utils';

/**
 * Hook to get current theme configuration
 */
export const useTheme = (): ExtendedThemeConfig => {
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light') as ThemeMode;

  return useMemo(() => getThemeConfig(themeMode), [themeMode]);
};

/**
 * Hook to get styled-components theme props
 */
export const useStyledTheme = () => {
  const theme = useTheme();
  return useMemo(() => createThemeProps(theme), [theme]);
};

/**
 * Hook to get ECharts theme configuration
 */
export const useEChartsTheme = () => {
  const theme = useTheme();
  return useMemo(() => createEChartsTheme(theme), [theme]);
};

/**
 * Hook to get AG-Grid theme configuration
 */
export const useAgGridTheme = (baseTheme: any) => {
  const theme = useTheme();
  return useMemo(() => createAgGridTheme(theme, baseTheme), [theme, baseTheme]);
};

/**
 * Hook to get theme mode only
 */
export const useThemeMode = (): ThemeMode => {
  return useSelector((state: RootState) => state.theme?.mode || 'light') as ThemeMode;
};
